import { Link, useRouter } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";
import { toast } from "sonner";
import { useForgotPassword } from "@wacht/tanstack-router";

const ResetPassword = () => {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState<string[]>(new Array(6).fill(""));
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState<"email" | "code" | "password">("email");
  const [error, setError] = useState("");
  const { forgotPassword, resetPassword, verifyOtp } = useForgotPassword();
  const [passwordResetToken, setPasswordResetToken] = useState("");

  const router = useRouter();

  const handleSendResetCode = async () => {
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      const resetAttempt = await forgotPassword(email);

      if (resetAttempt.errors.length) {
        setError(resetAttempt.errors[0].message);
      } else {
        setStep("code");
      }
    } catch (err: any) {
      console.error("Reset code error:", err);
      const errorMessage =
        err?.errors?.[0]?.message || "Failed to send reset code";
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    const codeString = code.join("");
    if (!codeString || codeString.length !== 6) {
      toast.error("Please enter the complete verification code");
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      const response = await verifyOtp(email, codeString);
      if (response.errors.length) {
        setError(response.errors[0].message);
      } else {
        setPasswordResetToken(response.data.token);
      }

      setStep("password");
      toast.success("Code verified! Please enter your new password.");
    } catch (err: any) {
      console.error("Code verification error:", err);
      const errorMessage =
        err?.errors?.[0]?.message || "Invalid verification code";
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (index: number, value: string) => {
    if (value.length > 1) return; // Only allow single digit

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleCodeKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error("Please enter and confirm your new password");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      const response = await resetPassword(passwordResetToken, newPassword);
      if (response.errors.length) {
        setError(response.errors[0].message);
      } else {
        router.navigate({ to: "/dashboard" });
      }
    } catch (err: any) {
      console.error("Password reset error:", err);
      const errorMessage =
        err?.errors?.[0]?.message || "Failed to reset password";
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="bg-background p-2">
      <div className="relative grid grid-cols-1 lg:grid-cols-2 w-full">
        {/* Left Dark Section - Same as signin page */}
        <div className="relative flex-col bg-[#0a0a0a] text-white hidden lg:flex justify-between items-center rounded-2xl overflow-hidden h-[calc(100dvh-16px)]">
          <div className="absolute inset-0 w-full h-full p-10">
            <img
              src="/id_full_logo_white.png"
              alt="InboxDoctor"
              className="h-[50px] w-auto"
            />

            <div className="flex flex-col gap-2 relative top-[8%] lg:top-[10%] left-[8%] max-w-[500px]">
              <h1 className="text-3xl/[30px] xl:text-[40px]/[40px] font-bold">
                Reset Your Password
              </h1>
              <p className="text-lg text-gray-400">
                Follow the steps to securely reset your password and regain
                access to your account.
              </p>
            </div>
          </div>

          <img
            src="/auth-image.jpg"
            alt="InboxDoctor Interface"
            className="w-full h-full object-cover object-left-top"
          />
        </div>

        {/* Right Reset Password Section */}
        <div className="p-4 lg:p-8 h-[calc(100dvh-16px)] overflow-y-auto">
          <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:max-w-[400px] h-full">
            <div className="flex flex-col space-y-2 text-center lg:text-left">
              <div className="lg:hidden flex justify-center items-center mb-6">
                <img
                  src="/id_full_logo_black.png"
                  alt="InboxDoctor"
                  className="h-[50px] w-auto dark:hidden"
                />
                <img
                  src="/id_full_logo_white.png"
                  alt="InboxDoctor"
                  className="h-[50px] w-auto hidden dark:block"
                />
              </div>

              <h1 className="text-4xl font-semibold tracking-tight pb-14 text-center hidden lg:block">
                {step === "email" && "Forgot Password"}
                {step === "code" && "Enter Verification Code"}
                {step === "password" && "Create New Password"}
              </h1>

              <h3 className="text-2xl font-semibold tracking-tight">
                {step === "email" && "Find your account"}
                {step === "code" && "Check your email"}
                {step === "password" && "Set your new password"}
              </h3>

              <p className="text-sm text-muted-foreground">
                {step === "email" &&
                  "Enter the email associated with your account to change your password."}
                {step === "code" && (
                  <>
                    We sent a verification code to{" "}
                    <span className="text-primary hover:underline cursor-pointer font-medium">
                      {email}
                    </span>
                  </>
                )}
                {step === "password" && "Enter your new password below."}
              </p>
            </div>

            <div className="space-y-4">
              {/* Step 1: Email */}
              {step === "email" && (
                <>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="email" className="text-sm font-medium">
                        Email
                      </Label>
                      <Input
                        id="email"
                        placeholder="name@example.com"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        autoCapitalize="none"
                        autoComplete="email"
                        autoCorrect="off"
                        disabled={isLoading}
                        className="mt-2"
                      />
                    </div>

                    {error && (
                      <div className="text-center">
                        <p className="text-red-500 text-sm">{error}</p>
                      </div>
                    )}

                    <Button
                      onClick={handleSendResetCode}
                      disabled={isLoading}
                      className="w-full bg-primary hover:bg-primary/80"
                    >
                      {isLoading ? "Sending..." : "Send OTP"}
                    </Button>
                  </div>
                </>
              )}

              {/* Step 2: Verification Code */}
              {step === "code" && (
                <>
                  <div className="space-y-4">
                    <div className="text-center">
                      <div className="flex gap-3 justify-center">
                        {code.map((digit, index) => (
                          <Input
                            key={index}
                            id={`otp-input-${index}`}
                            type="text"
                            maxLength={1}
                            className="w-14 h-14 text-center text-lg font-semibold border-2 focus:border-primary focus:ring-2 focus:ring-primary/20"
                            value={digit}
                            onChange={(e) =>
                              handleCodeChange(index, e.target.value)
                            }
                            onKeyDown={(e) => handleCodeKeyDown(index, e)}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="text-right">
                      <button
                        type="button"
                        onClick={() => setStep("email")}
                        className="text-sm text-primary hover:underline font-medium"
                        disabled={isLoading}
                      >
                        Change email
                      </button>
                    </div>

                    {error && (
                      <div className="text-center">
                        <p className="text-red-500 text-sm">{error}</p>
                      </div>
                    )}

                    <Button
                      onClick={handleVerifyCode}
                      disabled={isLoading}
                      className="w-full bg-primary hover:bg-primary/80"
                    >
                      {isLoading ? "Verifying..." : "Verify Code"}
                    </Button>
                  </div>
                </>
              )}

              {/* Step 3: New Password */}
              {step === "password" && (
                <>
                  <div className="space-y-4">
                    <div>
                      <Label
                        htmlFor="newPassword"
                        className="text-sm font-medium"
                      >
                        New Password
                      </Label>
                      <Input
                        id="newPassword"
                        placeholder="Enter new password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={isLoading}
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label
                        htmlFor="confirmPassword"
                        className="text-sm font-medium"
                      >
                        Confirm Password
                      </Label>
                      <Input
                        id="confirmPassword"
                        placeholder="Confirm new password"
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={isLoading}
                        className="mt-2"
                      />
                    </div>

                    {error && (
                      <div className="text-center">
                        <p className="text-red-500 text-sm">{error}</p>
                      </div>
                    )}

                    <Button
                      onClick={handleResetPassword}
                      disabled={isLoading}
                      className="w-full bg-primary hover:bg-primary/80"
                    >
                      {isLoading ? "Resetting..." : "Reset Password"}
                    </Button>
                  </div>
                </>
              )}

              <div className="text-center">
                <Link
                  to="/signin"
                  className="text-sm text-primary hover:underline font-medium"
                >
                  Back to login
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ResetPassword;
